import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ExternalLink, Loader2, ArrowLeft } from 'lucide-react';
import { useShop } from '../context/ShopContext';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_URL = process.env.REACT_APP_API_URL;

export default function ContactPage() {
    const { merchantId, shopName: contextShopName } = useShop();
    const [storeInfo, setStoreInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);

    useEffect(() => {
        if (!merchantId) return;
        const fetchStoreInfo = async () => {
            try {
                const response = await axios.get(`${API_URL}/store/${merchantId}/info`);
                setStoreInfo(response.data);
            } catch (error) {
                console.error('Failed to fetch store info:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchStoreInfo();
    }, [merchantId]);

    const handleNewsletterSubmit = (e) => {
        e.preventDefault();
        if (storeInfo?.newsletter_embed_url && email) {
            const url = new URL(storeInfo.newsletter_embed_url);
            url.searchParams.set('email', email);
            window.open(url.toString(), '_blank');
            setSubscribed(true);
            setEmail('');
        }
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <Header />
                <main className="flex-1 flex items-center justify-center min-h-[400px]">
                    <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </main>
                <Footer />
            </div>
        );
    }

    if (!storeInfo) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <Header />
                <main className="flex-1 flex flex-col items-center justify-center py-20 px-4">
                    <p className="text-muted-foreground mb-4">Δεν βρέθηκαν στοιχεία καταστήματος</p>
                    <Link
                        to="/"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full border hover:bg-muted transition-colors"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Επιστροφή στο κατάστημα
                    </Link>
                </main>
                <Footer />
            </div>
        );
    }

    const shopName = contextShopName || storeInfo.shop_name || 'Shop';
    const hasContactInfo = storeInfo.address || storeInfo.phone || storeInfo.mobile || storeInfo.email;
    const hasMap = storeInfo.google_maps_embed || (storeInfo.address && storeInfo.city);
    const hasNewsletter = storeInfo.newsletter_embed_url;

    return (
        <div className="min-h-screen bg-background flex flex-col" data-testid="storefront-contact-page">
            <Header />
            <main className="flex-1 max-w-4xl w-full mx-auto px-4 py-6 sm:py-8 space-y-6">
                <Link
                    to="/"
                    className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors text-sm font-medium"
                >
                    <ArrowLeft className="w-4 h-4" />
                    Επιστροφή στο κατάστημα
                </Link>

                <div className="space-y-8">
                    <div className="text-center">
                        <h1 className="text-3xl font-bold text-primary mb-2">Επικοινωνία</h1>
                        {storeInfo.shop_description && (
                            <p className="text-muted-foreground max-w-xl mx-auto">{storeInfo.shop_description}</p>
                        )}
                    </div>

                    <div className="grid md:grid-cols-2 gap-8">
                {hasContactInfo && (
                    <div className="bg-card rounded-xl border p-6 space-y-6">
                        <h2 className="text-xl font-semibold text-primary">{shopName}</h2>

                        {(storeInfo.address || storeInfo.city) && (
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <MapPin className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="font-medium">Διεύθυνση</p>
                                    <p className="text-muted-foreground">
                                        {storeInfo.address && <>{storeInfo.address}<br /></>}
                                        {storeInfo.city && <>{storeInfo.city} {storeInfo.postal_code}</>}
                                        {storeInfo.country && <><br />{storeInfo.country}</>}
                                    </p>
                                </div>
                            </div>
                        )}

                        {(storeInfo.phone || storeInfo.mobile) && (
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <Phone className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="font-medium">Τηλέφωνο</p>
                                    {storeInfo.phone && (
                                        <a href={`tel:${storeInfo.phone}`} className="text-muted-foreground hover:text-primary transition-colors block">
                                            {storeInfo.phone}
                                        </a>
                                    )}
                                    {storeInfo.mobile && (
                                        <a href={`tel:${storeInfo.mobile}`} className="text-muted-foreground hover:text-primary transition-colors block">
                                            {storeInfo.mobile} (κινητό)
                                        </a>
                                    )}
                                </div>
                            </div>
                        )}

                        {storeInfo.email && (
                            <div className="flex items-start gap-4">
                                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                                    <Mail className="w-5 h-5 text-primary" />
                                </div>
                                <div>
                                    <p className="font-medium">Email</p>
                                    <a href={`mailto:${storeInfo.email}`} className="text-muted-foreground hover:text-primary transition-colors">
                                        {storeInfo.email}
                                    </a>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {hasMap && (
                    <div className="bg-card rounded-xl border overflow-hidden">
                        <iframe
                            src={`https://www.google.com/maps?q=${encodeURIComponent(
                                storeInfo.address && storeInfo.city
                                    ? `${storeInfo.address}, ${storeInfo.city}${storeInfo.country ? ', ' + storeInfo.country : ''}`
                                    : storeInfo.shop_name || ''
                            )}&output=embed`}
                            width="100%"
                            height="100%"
                            style={{ border: 0, minHeight: '300px' }}
                            allowFullScreen=""
                            loading="lazy"
                            referrerPolicy="no-referrer-when-downgrade"
                            title="Store Location"
                        />
                    </div>
                    )}
                </div>

                {hasNewsletter && (
                    <div className="bg-gradient-to-r from-primary/5 to-primary/10 rounded-xl p-8 text-center">
                        <h2 className="text-2xl font-semibold text-primary mb-2">Εγγραφείτε στο Newsletter</h2>
                        <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                            Μάθετε πρώτοι για νέα προϊόντα, προσφορές και εκπτώσεις!
                        </p>
                        {subscribed ? (
                            <p className="text-green-600 font-medium">Ευχαριστούμε! Ελέγξτε το email σας.</p>
                        ) : (
                            <form onSubmit={handleNewsletterSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Το email σας..."
                                    required
                                    className="flex-1 h-10 px-3 rounded-md border bg-background"
                                    data-testid="newsletter-email-input"
                                />
                                <button type="submit" className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-full bg-primary text-primary-foreground hover:opacity-90 transition-opacity" data-testid="newsletter-submit-btn">
                                    Εγγραφή
                                    <ExternalLink className="w-4 h-4" />
                                </button>
                            </form>
                        )}
                    </div>
                )}
                </div>
            </main>
            <Footer />
        </div>
    );
}
